import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Trash2,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ProductMatchAutocomplete from '@/components/ProductMatchAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNIT_OPTIONS = ['each', 'ft', 'box', 'spool', 'roll', 'bundle', 'pair', 'lf'];

const STATUS_STYLES = {
  'Not Ordered':       'bg-zinc-200 text-zinc-700 border-zinc-400',
  'Partially Ordered': 'bg-yellow-100 text-yellow-800 border-yellow-500',
  'Fully Ordered':     'bg-blue-100 text-blue-800 border-blue-500',
  'Partially Delivered': 'bg-orange-100 text-orange-800 border-orange-500',
  'Fully Delivered':   'bg-green-100 text-green-800 border-green-600',
};

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n || 0);

/**
 * Bill of Materials section embedded in the expanded phase detail (List + Chart views).
 * Handles fetching, listing, inline-editing, adding (via product autocomplete), and
 * deleting BOM items, plus a summary footer with estimated vs actual cost variance.
 */
const BomSection = ({ phaseId, canEdit = true, onChange }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');

  // Add dialog state
  const [newItem, setNewItem] = useState({
    product_id: null,
    product_description: '',
    product_canonical_name: '',
    required_quantity: 1,
    unit: 'each',
    estimated_unit_price: 0,
    notes: '',
  });

  const refresh = useCallback(async () => {
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/phases/${phaseId}/bom`, { withCredentials: true }),
        axios.get(`${API}/phases/${phaseId}/bom/summary`, { withCredentials: true }),
      ]);
      setItems(listRes.data || []);
      setSummary(sumRes.data || null);
      if (onChange) onChange();
    } catch (err) {
      console.error('Failed to load BOM', err);
    } finally {
      setLoading(false);
    }
  }, [phaseId, onChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = async () => {
    if (!newItem.product_id) {
      toast.error('Pick a product from the catalog first');
      return;
    }
    if (!newItem.required_quantity || newItem.required_quantity <= 0) {
      toast.error('Required quantity must be > 0');
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API}/phases/${phaseId}/bom`,
        {
          product_id: newItem.product_id,
          required_quantity: parseFloat(newItem.required_quantity),
          unit: newItem.unit,
          estimated_unit_price: parseFloat(newItem.estimated_unit_price) || null,
          notes: newItem.notes || null,
        },
        { withCredentials: true },
      );
      toast.success('Material added to BOM');
      setShowAdd(false);
      setNewItem({
        product_id: null,
        product_description: '',
        product_canonical_name: '',
        required_quantity: 1,
        unit: 'each',
        estimated_unit_price: 0,
        notes: '',
      });
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add BOM item');
    } finally {
      setAdding(false);
    }
  };

  const handleInlineSave = async (item, field, value) => {
    const payload = {};
    payload[field] = field === 'required_quantity' || field === 'estimated_unit_price'
      ? parseFloat(value) || 0
      : value;
    try {
      await axios.put(
        `${API}/phases/${phaseId}/bom/${item.bom_item_id}`,
        payload,
        { withCredentials: true },
      );
      await refresh();
    } catch {
      toast.error('Failed to save');
    } finally {
      setEditingCell(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.product_name}" from the BOM?`)) return;
    try {
      await axios.delete(
        `${API}/phases/${phaseId}/bom/${item.bom_item_id}`,
        { withCredentials: true },
      );
      toast.success('Material removed');
      await refresh();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="border-t-2 border-black pt-4" data-testid={`bom-section-${phaseId}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-zinc-700" />
          <h4 className="font-heading font-bold text-sm uppercase tracking-wider">Materials (BOM)</h4>
          {summary && (
            <span className="text-xs font-mono text-zinc-500">
              {summary.total_items} item{summary.total_items !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAdd(true)}
            data-testid={`bom-add-btn-${phaseId}`}
            className="h-12 min-h-[48px] px-4 bg-primary text-zinc-900 font-bold uppercase border-2 border-black hover:bg-primary-hover transition-colors flex items-center gap-2 text-xs"
          >
            <Plus className="w-4 h-4" />
            Add Material
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-6">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-6 text-center" data-testid={`bom-empty-${phaseId}`}>
          <Package className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
          <p className="text-sm text-zinc-500">No materials defined for this phase yet.</p>
          {canEdit && (
            <p className="text-xs text-zinc-400 mt-1">Click "Add Material" to plan what this phase needs.</p>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-zinc-200 bg-white">
            <table className="w-full min-w-[640px] text-sm" data-testid={`bom-table-${phaseId}`}>
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Product</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Required</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Ordered</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Delivered</th>
                  <th className="text-center text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Status</th>
                  <th className="text-right text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 px-3 py-2">Est. Cost</th>
                  {canEdit && <th className="px-2 py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const editing = editingCell?.id === it.bom_item_id;
                  return (
                    <tr key={it.bom_item_id} className="border-b border-zinc-100" data-testid={`bom-row-${it.bom_item_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-zinc-900 text-sm">{it.product_name}</p>
                        {it.category && (
                          <span className="inline-block mt-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-300">
                            {it.category}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {canEdit && editing && editingCell.field === 'required_quantity' ? (
                          <input
                            type="number"
                            step="0.01"
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleInlineSave(it, 'required_quantity', editValue)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(it, 'required_quantity', editValue); }}
                            data-testid={`bom-edit-qty-${it.bom_item_id}`}
                            className="w-20 h-8 border border-primary px-1 text-right font-mono"
                          />
                        ) : (
                          <button
                            onClick={() => canEdit && (setEditingCell({ id: it.bom_item_id, field: 'required_quantity' }), setEditValue(it.required_quantity))}
                            data-testid={`bom-qty-${it.bom_item_id}`}
                            className={`${canEdit ? 'hover:bg-zinc-100' : ''} px-1 py-0.5`}
                          >
                            {it.required_quantity} {it.unit}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-zinc-600" data-testid={`bom-ordered-${it.bom_item_id}`}>
                        {it.ordered_quantity || 0}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-zinc-900 font-bold" data-testid={`bom-delivered-${it.bom_item_id}`}>
                        {it.fulfilled_quantity || 0}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] font-heading font-bold uppercase tracking-wider border ${STATUS_STYLES[it.fulfillment_status] || STATUS_STYLES['Not Ordered']}`}
                          data-testid={`bom-status-${it.bom_item_id}`}
                        >
                          {it.fulfillment_status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {canEdit && editing && editingCell.field === 'estimated_unit_price' ? (
                          <input
                            type="number"
                            step="0.01"
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleInlineSave(it, 'estimated_unit_price', editValue)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(it, 'estimated_unit_price', editValue); }}
                            data-testid={`bom-edit-price-${it.bom_item_id}`}
                            className="w-24 h-8 border border-primary px-1 text-right font-mono"
                          />
                        ) : (
                          <button
                            onClick={() => canEdit && (setEditingCell({ id: it.bom_item_id, field: 'estimated_unit_price' }), setEditValue(it.estimated_unit_price ?? 0))}
                            data-testid={`bom-price-${it.bom_item_id}`}
                            className={`${canEdit ? 'hover:bg-zinc-100' : ''} px-1 py-0.5`}
                          >
                            {it.estimated_total != null ? fmtCurrency(it.estimated_total) : '—'}
                          </button>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-2 py-2">
                          <button
                            onClick={() => handleDelete(it)}
                            data-testid={`bom-delete-${it.bom_item_id}`}
                            className="p-1.5 text-zinc-400 hover:text-destructive"
                            title="Remove from BOM"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {summary && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`bom-summary-${phaseId}`}>
              <div className="bg-white border border-zinc-200 p-2">
                <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500">Estimated</p>
                <p className="font-mono font-bold text-sm text-zinc-900">{fmtCurrency(summary.estimated_total_cost)}</p>
              </div>
              <div className="bg-white border border-zinc-200 p-2">
                <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500">Actual (Delivered)</p>
                <p className="font-mono font-bold text-sm text-zinc-900">{fmtCurrency(summary.actual_cost)}</p>
              </div>
              <div className="bg-white border border-zinc-200 p-2">
                <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500">Variance</p>
                <p className={`font-mono font-bold text-sm flex items-center gap-1 ${
                  summary.variance > 0 ? 'text-red-600' : summary.variance < 0 ? 'text-emerald-600' : 'text-zinc-500'
                }`}>
                  {summary.variance > 0 ? <TrendingUp className="w-3 h-3" /> : summary.variance < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                  {fmtCurrency(Math.abs(summary.variance))}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Material dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md border border-zinc-200 rounded-none p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Add Material</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-4 space-y-3">
            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Product</label>
              <ProductMatchAutocomplete
                value={newItem.product_description}
                onChange={(v) => setNewItem(prev => ({ ...prev, product_description: v }))}
                onProductSelect={(sel) => setNewItem(prev => ({
                  ...prev,
                  product_id: sel.product_id,
                  product_description: sel.product_description,
                  product_canonical_name: sel.product_description,
                  unit: sel.unit_of_measure || prev.unit,
                }))}
                currentProductId={newItem.product_id}
                placeholder="Search the catalog..."
                testIdPrefix="bom-add-product"
                inputClassName="w-full h-12 border border-zinc-200 bg-white text-sm px-3 focus:outline-none focus:border-primary"
              />
              {newItem.product_id && (
                <p className="text-xs text-emerald-700 mt-1">✓ Catalog match: {newItem.product_canonical_name}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Required Qty</label>
                <input
                  type="number"
                  step="0.01"
                  value={newItem.required_quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, required_quantity: e.target.value }))}
                  data-testid="bom-add-qty"
                  className="w-full h-12 border border-zinc-200 bg-white px-3 font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Unit</label>
                <select
                  value={newItem.unit}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                  data-testid="bom-add-unit"
                  className="w-full h-12 border border-zinc-200 bg-white px-3"
                >
                  {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Est. Unit Price (optional)</label>
              <input
                type="number"
                step="0.01"
                value={newItem.estimated_unit_price}
                onChange={(e) => setNewItem(prev => ({ ...prev, estimated_unit_price: e.target.value }))}
                data-testid="bom-add-price"
                className="w-full h-12 border border-zinc-200 bg-white px-3 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={newItem.notes}
                onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="bom-add-notes"
                className="w-full h-12 border border-zinc-200 bg-white px-3"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowAdd(false)}
                className="flex-1 h-12 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={adding || !newItem.product_id}
                data-testid="bom-add-confirm"
                className="flex-1 h-12 bg-primary text-black font-heading font-bold uppercase tracking-wider border border-zinc-200 hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Material'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BomSection;
